import { useState, useCallback, forwardRef, ComponentRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DebouncedAutocomplete } from 'components';
import OptionLabel from './components/OptionLabel';

import useNotifications from 'hooks/common/useNotifications';
import usePartnerSchemesQuery from 'hooks/requests/query/usePartnerSchemesQuery';

import { DefaultPagination } from 'constants/pagination';

import { PartnerSchemeStatusType, PartnerSchemeType } from 'types/partnerSchemes';
import { ISchemeAutocompleteProps } from './SchemeAutocomplete.types';

import './SchemeAutocomplete.styles.scss';

const DEFAULT_STATUS = Object.values(PartnerSchemeStatusType);

const SchemeAutocomplete = forwardRef<ComponentRef<typeof DebouncedAutocomplete>, ISchemeAutocompleteProps>(
  ({ value, status = DEFAULT_STATUS, disableDeleted, onSelect, onChange, ...props }: ISchemeAutocompleteProps, ref) => {
    const [search, setSearch] = useState(value?.name || value || '');
    const [options, setOptions] = useState([]);

    const { t } = useTranslation();
    const { notificationApi } = useNotifications();

    const { loading, refetch: getPartnerSchemes } = usePartnerSchemesQuery({
      refetchOnMount: false,
      params: { page: DefaultPagination.START_PAGE },
    });

    const generateOption = useCallback(
      (scheme: PartnerSchemeType) => {
        const disabled = disableDeleted && scheme.status === PartnerSchemeStatusType.DELETED;

        return {
          value: JSON.stringify(scheme),
          label: <OptionLabel name={scheme.name} isDefault={scheme.isdefault} disabled={disabled} />,
          disabled,
        };
      },
      [disableDeleted],
    );

    const getSchemes = useCallback(
      async (search: string) => {
        if (!search) {
          return;
        }

        setOptions([]);

        const {
          data: schemes,
          success,
          errorMessage,
        } = await getPartnerSchemes({ search, page: DefaultPagination.START_PAGE, status });

        if (success) {
          const newOptions = schemes.data.map(scheme => generateOption(scheme));

          setOptions(newOptions);
        } else {
          notificationApi.error({ title: t(errorMessage) });
        }
      },
      [notificationApi, status, t, getPartnerSchemes, generateOption],
    );

    const handleSelect = async (value: string) => {
      const scheme = JSON.parse(value);
      const name: string = scheme.name || '';

      onSelect && onSelect(scheme);
      onChange && onChange(scheme);
      setSearch(name);
    };

    const handleChange = async (value: string) => {
      setSearch(value);
    };

    return (
      <DebouncedAutocomplete
        autoFocus
        value={search}
        options={options}
        onSelect={handleSelect}
        onChange={handleChange}
        onSearch={getSchemes}
        loading={loading}
        className="np-scheme-autocomplete"
        ref={ref}
        {...props}
      />
    );
  },
);

export default SchemeAutocomplete;
